/* eslint-disable */

import { ClockIcon, PencilIcon as PencilIconOutline } from "@heroicons/react/outline";
import { ClockIcon as ClockIconSolid, PencilIcon as PencilIconSolid } from "@heroicons/react/solid";
import posthog from "posthog-js";
import { createContext, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Outlet, useLocation, useParams, useSearchParams } from "react-router-dom";

import DatasetActionButtons from "./Header/DatasetActionButtons";
import { useActions } from "./hooks/useActions";
import { useData } from "./hooks/useData";
import { useDatasetUploadProgress } from "./hooks/useDatasetUploadProgress";
import { useDeleteRowsProgress } from "./hooks/useDeleteRowsProgress";
import { useNewVersionProgress } from "./hooks/useNewVersionProgress";
import { useSaveProgress } from "./hooks/useSaveProgress";

import { FolderScopeProvider } from "@/components/ChooseEntityDialog/folder-scope-context-provider";
import { ConfirmationModal, ConfirmationModalVariant } from "@/components/ConfirmationModal";
import LegacyEntityDeprecationBanner from "@/components/LegacyEntityDeprecationBanner";
import OverwriteDraftDatasetModal from "@/components/OverwriteDraftDatasetModal";
import { BadgeVariant } from "@/components/ui/Badge";
import { ResourceHeader } from "@/components/ui/ResourceHeader";
import { TabItem } from "@/components/ui/TabNavigation/types";
import BreadcrumbsPageWrapper from "@/components/UnifiedRegistry/BreadcrumbsPageWrapper";
import useFolderPathBreadcrumbs from "@/components/UnifiedRegistry/hooks/useFolderPathBreadcrumbs";
import { useAuth } from "@/context/auth-context";
import { useNavigateWithSearch } from "@/hooks";
import { useLegacyEntityTableConversion } from "@/hooks/useLegacyEntityTableConversion";
import { EntityType, useDatasets, useGetChangelogPendingCount } from "@/queries";
import { useDatasetExportProgress, useDatasetRows, useEditDatasetGroup } from "@/queries/datasets";
import { DatasetGroupRead } from "@/types/dataset-groups";
import { DatasetRowRead } from "@/types/dataset-rows";
import { DatasetRead } from "@/types/datasets";
import { formatTimestamp } from "@/utils/timestamp";

export type ProcessingType = "upload" | "newVersion" | "save" | "deleteRows" | "export" | null;

interface DatasetEditorContextType {
  isDraft: boolean;
  isDeletingDataset: boolean;
  isDeletingDatasetGroup: boolean;
  isSavingDataset: boolean;
  isSavingCell: boolean;
  datasetGroup?: DatasetGroupRead;
  isSettingDraftDatasetStateFromDataset: boolean;
  setSavingCell: (bool: boolean) => void;
  handleEditClick: () => void;
  handleDeleteClick: () => void;
  handleSaveAsNewVersionClick: (onSuccess?: () => void) => void;
  handleDatasetGroupNameEdit: ({
    onSuccess,
    onError,
    newTitle,
  }: {
    onSuccess?: () => void;
    onError?: () => void;
    newTitle: string;
  }) => void;
  handleDeleteDatasetGroup: () => void;
  setPage: (page: number) => void;
  setPageSize: (pageSize: number) => void;
  page: number;
  pageSize: number;
  pages: number;
  total: number;
  rows: DatasetRowRead[];
  datasets: DatasetRead[];
  isLoading: boolean;
  activeDataset?: DatasetRead;
  processingProgress: number;
  processingType: ProcessingType | null;
  setProcessingProgress: (progress: number) => void;
  setProcessingType: (type: ProcessingType) => void;
  isUploadingToS3: boolean;
  setIsUploadingToS3: (uploading: boolean) => void;
  isRowsEmpty: boolean;
  currentDataset?: DatasetRead;
  highlightedRowId?: number;
}

export const DatasetEditorContext = createContext<DatasetEditorContextType | undefined>(undefined);

const DatasetEditor = () => {
  const hasTracked = useRef(false);

  useEffect(() => {
    if (!hasTracked.current) {
      posthog.capture("Datasets Page Viewed", {
        name: "Datasets",
        href: window.location.href,
      });
      hasTracked.current = true;
    }
  }, []);

  const [isOverwriteDraftDatasetModalOpen, setOverwriteDraftDatasetModalOpen] = useState(false);
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(20);
  const [isConfirmExitModalOpen, setIsConfirmExitModalOpen] = useState(false);
  const [processingProgress, setProcessingProgress] = useState<number>(0);
  const [processingType, setProcessingType] = useState<ProcessingType>(null);
  const [isUploadingToS3, setIsUploadingToS3] = useState<boolean>(false);
  const [isDiscarded, setIsDiscarded] = useState(false);

  const { datasetId, datasetGroupId, workspaceId } = useParams();

  const datasetGroupNumericId = datasetGroupId ? Number(datasetGroupId) : undefined;
  const validDatasetGroupId =
    typeof datasetGroupNumericId === "number" && Number.isFinite(datasetGroupNumericId)
      ? datasetGroupNumericId
      : undefined;

  const { convertToTable: convertDatasetToTable, isConverting: isConvertingDataset } =
    useLegacyEntityTableConversion({
      sourceType: "dataset_group",
      sourceId: validDatasetGroupId,
    });

  const authContext = useAuth();
  const userToken = authContext!.userToken;

  const navigateWithSearch = useNavigateWithSearch();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // Get highlighted row ID from URL params - capture once on mount
  const getInitialRowId = () => {
    const rowParam = searchParams.get("row");
    return rowParam ? parseInt(rowParam, 10) : undefined;
  };
  const [highlightedRowId] = useState<number | undefined>(getInitialRowId);

  const datasetsQuery = useDatasets(userToken!, datasetGroupId!);

  // Pass targetRowId to backend - it will return the page containing that row
  const datasetRowsQuery = useDatasetRows({
    userToken: userToken!,
    dataset_id: parseInt(datasetId!, 10),
    page: page,
    pageSize: pageSize,
    sortBy: undefined,
    sortOrder: undefined,
    targetRowId: highlightedRowId,
  });

  // When targetRowId is set, use the page from the response (backend calculates correct page)
  // This avoids a refetch that would cause jitter
  const currentPage =
    highlightedRowId && datasetRowsQuery.data?.success ? datasetRowsQuery.data.page : page;

  const {
    datasetGroup,
    isLoading: datasetGroupLoading,
    datasets,
    isDraft,
    nonDraftVersionsExist,
    rows,
    activeDataset,
  } = useData(
    userToken!,
    parseInt(datasetId!, 10),
    parseInt(datasetGroupId!, 10),
    datasetsQuery,
    datasetRowsQuery,
  );

  const folderId = datasetGroup?.folder_id;

  const { mutate: editDatasetGroupTags } = useEditDatasetGroup(userToken!, folderId);

  const handleTagsEdit = useCallback(
    (tags: string[]) => {
      if (!datasetGroupId) {
        return;
      }
      editDatasetGroupTags({
        datasetGroupId: parseInt(datasetGroupId, 10),
        tags,
      });
    },
    [datasetGroupId, editDatasetGroupTags],
  );

  const {
    isDeletingDataset,
    isDeletingDatasetGroup,
    isSavingDataset,
    isSettingDraftDatasetStateFromDataset,
    handleEditClick,
    handleDeleteClick,
    handleSaveAsNewVersionClick,
    handleDatasetGroupNameEdit,
    handleDeleteDatasetGroup,
  } = useActions(
    workspaceId,
    datasets,
    datasetId,
    datasetGroupId,
    userToken,
    nonDraftVersionsExist,
    setOverwriteDraftDatasetModalOpen,
    setProcessingType,
    folderId,
  );

  const isNavigationBlocked =
    (isDraft && !isDeletingDataset && !isDeletingDatasetGroup && !isDiscarded) || isUploadingToS3;

  const modalContent = useMemo(() => {
    if (isUploadingToS3) {
      return {
        title: "Your dataset is being uploaded",
        description: "Are you sure you want to leave this page?",
        leaveButtonText: "Leave",
        saveButtonText: "Stay",
      };
    }
    return {
      title: "Unsaved Changes",
      description:
        "If you exit now, your dataset will remain in draft mode. Would you like to save your changes or leave it as draft?",
      leaveButtonText: "Leave as Draft",
      saveButtonText: "Save & Continue",
    };
  }, [isUploadingToS3]);

  const isLoading = useMemo(
    () => datasetsQuery.isFetching || datasetsQuery.isLoading,
    [datasetsQuery.isFetching, datasetsQuery.isLoading],
  );

  const [isSavingCell, setSavingCell] = useState(false);
  const isRowsEmpty = useMemo(
    () => rows.every((row: { dataset_row_id: number }) => row.dataset_row_id === -1),
    [rows],
  );

  // Redirect to first dataset if no datasetId in URL
  useEffect(() => {
    if (!datasetId && datasets?.length) {
      const mostRecentDataset = datasets[0];
      const isChangelogPath = location.pathname.endsWith("/changelog");
      const changelogSuffix = isChangelogPath ? "/changelog" : "";
      navigateWithSearch(
        `/workspace/${workspaceId}/dataset-groups/${datasetGroupId}/dataset/${mostRecentDataset.id}${changelogSuffix}`,
        { replace: true },
      );
    }
  }, [datasetId, datasets, navigateWithSearch, workspaceId, datasetGroupId, location.pathname]);

  // Determine if we're on the changelog tab based on URL (used for conditional UI like badge/action buttons)
  const isChangelogTab = location.pathname.endsWith("/changelog");

  const { data: pendingChangelogCount } = useGetChangelogPendingCount({
    entityId: Number(datasetGroupId),
    entityType: EntityType.DATASET,
    enabled: !!datasetGroupId,
  });

  const tabs = useMemo<TabItem[]>(
    () => [
      {
        id: 0,
        name: "Editor",
        outlineIcon: <PencilIconOutline className="mr-1.5 h-4 w-4" aria-hidden="true" />,
        solidIcon: <PencilIconSolid className="mr-1.5 h-4 w-4" aria-hidden="true" />,
        content: <Outlet />,
        path: `/workspace/${workspaceId}/dataset-groups/${datasetGroupId}/dataset/${datasetId}`,
      },
      {
        id: 1,
        name: "Changelog",
        outlineIcon: <ClockIcon className="mr-1.5 h-4 w-4" aria-hidden="true" />,
        solidIcon: <ClockIconSolid className="mr-1.5 h-4 w-4" aria-hidden="true" />,
        count: pendingChangelogCount,
        content: <Outlet />,
        path: `/workspace/${workspaceId}/dataset-groups/${datasetGroupId}/dataset/${datasetId}/changelog`,
      },
    ],
    [workspaceId, datasetGroupId, datasetId, pendingChangelogCount],
  );

  const breadcrumbProps = useFolderPathBreadcrumbs(
    datasetGroup?.folder_id ? Number(datasetGroup?.folder_id) : null,
    datasetGroup?.name || "",
  );

  return (
    <DatasetEditorContext.Provider
      value={{
        isDraft,
        isSavingCell,
        setSavingCell,
        isDeletingDataset,
        isDeletingDatasetGroup,
        isSavingDataset,
        datasetGroup,
        isSettingDraftDatasetStateFromDataset,
        handleEditClick,
        handleDeleteClick,
        handleSaveAsNewVersionClick,
        handleDatasetGroupNameEdit,
        handleDeleteDatasetGroup,
        setPage,
        setPageSize,
        page: currentPage,
        pageSize,
        pages: datasetRowsQuery.data?.pages || 0,
        total: datasetRowsQuery.data?.total || 0,
        rows,
        datasets,
        isLoading,
        activeDataset,
        processingProgress,
        processingType,
        setProcessingProgress,
        setProcessingType,
        isRowsEmpty,
        isUploadingToS3,
        setIsUploadingToS3,
        currentDataset: activeDataset,
        highlightedRowId,
      }}
    >
      <FolderScopeProvider value={datasetGroup?.folder_id ?? null}>
        <div className="flex h-full flex-col">
          <BreadcrumbsPageWrapper
            {...breadcrumbProps}
            className="flex h-full flex-col pb-3"
            entityTitle={{
              type: EntityType.DATASET,
              title: datasetGroup?.name || "",
              onTitleEdit: handleDatasetGroupNameEdit,
              badgeText: !isChangelogTab && isDraft ? "Draft" : "",
              badgeVariant: BadgeVariant.ORANGE,
              isLoading: datasetGroupLoading,
            }}
          >
            <div className="mb-3">
              <LegacyEntityDeprecationBanner
                legacyEntityName="datasets"
                onConvert={convertDatasetToTable}
                isConverting={isConvertingDataset}
              />
            </div>

            <ProgressListeners />
            <ConfirmationModal
              title={modalContent.title}
              description={modalContent.description}
              isNavigationBlocked={isNavigationBlocked}
              leaveButtonText={modalContent.leaveButtonText}
              saveButtonText={modalContent.saveButtonText}
              handleOnConfirm={(_nextLocationPathname, proceedWithNavigation) => {
                if (isUploadingToS3) {
                  // For upload scenario, "Stay" just closes the modal - no navigation
                  setIsConfirmExitModalOpen(false);
                  return;
                }
                // For draft scenario, save and continue
                // After save completes, proceed with navigation
                handleSaveAsNewVersionClick?.(() => {
                  setIsConfirmExitModalOpen(false);
                  proceedWithNavigation?.();
                });
              }}
              handleLeave={() => {
                // Modal handles navigation via blocker.proceed() internally
              }}
              isLoading={isSavingDataset && !isUploadingToS3}
              isOpen={isConfirmExitModalOpen}
              setIsOpen={setIsConfirmExitModalOpen}
              headerVariant={ConfirmationModalVariant.warning}
              useBlocker={isNavigationBlocked}
            />
            <ResourceHeader
              type={EntityType.DATASET}
              title={datasetGroup?.name || ""}
              onTitleEdit={handleDatasetGroupNameEdit}
              tags={datasetGroup?.tags || []}
              onTagsEdit={handleTagsEdit}
              id={datasetGroup?.id || ""}
              createdAt={datasetGroup?.created_at ? formatTimestamp(datasetGroup.created_at) : ""}
              badgeText={!isChangelogTab && isDraft ? "Draft" : ""}
              badgeVariant={BadgeVariant.ORANGE}
              isLoading={datasetGroupLoading}
              titleInBreadcrumb
              minimal
              tabsProps={{
                tabs: tabs,
                actionButtons: !isChangelogTab ? (
                  <DatasetActionButtons
                    handleDeleteDatasetGroup={handleDeleteDatasetGroup ?? (() => undefined)}
                    onDiscard={() => setIsDiscarded(true)}
                  />
                ) : null,
              }}
            />
          </BreadcrumbsPageWrapper>
        </div>
      </FolderScopeProvider>
      <OverwriteDraftDatasetModal
        datasets={datasets}
        isOpen={isOverwriteDraftDatasetModalOpen}
        setOpen={setOverwriteDraftDatasetModalOpen}
      />
    </DatasetEditorContext.Provider>
  );
};

// Separate component to use the hooks inside UserChannelProvider
const ProgressListeners = () => {
  useNewVersionProgress();
  useSaveProgress();
  useDatasetUploadProgress();
  useDeleteRowsProgress();
  useDatasetExportProgress();
  return null;
};

export default DatasetEditor;
